import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { getOrder, postAbortPayment } from '@/api/endpoints/billing'
import { ApiError, isNetworkError } from '@/api/errors'
import { BILLING_ERROR_CODES, type FailureReason, type OrderResponse, type OrderStatus } from '@/api/types'
import { STORAGE_KEYS } from '@/constants/storageKeys'
import { trackPaymentFailed, trackPaymentSuccess } from '@/services/metrikaGoals'
import { pollUntil } from '@/services/polling'
import { useProfileStore } from '@/stores/profile'
import { useUsageStore } from '@/stores/usage'

export type PaymentState = 'idle' | 'loadingSdk' | 'widget' | 'confirming' | 'succeeded' | 'failed' | 'unknown'

/**
 * Состояния, у которых есть что показать человеку (см. PaymentDialog). Всё остальное шторка
 * не рисует: пока грузится SDK или открыт виджет, поверх него показывать нечего, а у unknown
 * своего экрана пока нет.
 */
const DIALOG_STATES: PaymentState[] = ['confirming', 'succeeded', 'failed']

/** Первую минуту спрашиваем часто: в неё укладывается почти каждый платёж. */
const FAST_INTERVAL_MS = 3000
const FAST_WINDOW_MS = 60000
const SLOW_INTERVAL_MS = 10000

/** Если бэк не прислал expires_at, поллим не дольше этого: вечный поллер недопустим. */
const FALLBACK_TTL_MS = 3 * 60 * 1000

/**
 * Таймаут проверки заказа перед отменой. Свой, как и у abort: глобального у axios-инстанса
 * нет, а зависший запрос заперал бы кнопку оплаты до перезагрузки страницы.
 */
const PAID_CHECK_TIMEOUT_MS = 5000

/**
 * Ошибку поллинга стоит переждать, если она обещает пройти сама: сеть отвалилась,
 * бэк перезапускается (5xx) или срезал частоту (429). Заказ от этого не изменился,
 * а бросить наблюдение за уже ушедшими деньгами из-за одного 502 нельзя — человек
 * останется без Pro и без объяснения.
 *
 * Всё остальное (404, 403, 400) — окончательный ответ, его разбирает handlePollError.
 */
function isTransientPollError(error: unknown): boolean {
	if (isNetworkError(error)) return true
	const status = error instanceof ApiError ? error.status : undefined
	if (status === undefined) return false
	return status === 429 || status >= 500
}

const KNOWN_FAILURE_REASONS: FailureReason[] = [
	'card_declined',
	'insufficient_funds',
	'expired_card',
	'3ds_failed',
	'cancelled_by_user',
	'provider_error',
	'timeout'
]

interface StoredOrder {
	orderId: string
	expiresAt: string | null
	/**
	 * Была ли попытка списания. Живёт в хранилище, а не выводится из статуса: бэк отдаёт
	 * один pending и на свежий заказ, и на заказ с ушедшими деньгами, поэтому различить
	 * их может только тот, кто видел платёжный виджет.
	 */
	attempted?: boolean
}

export const useBillingStore = defineStore('billing', () => {
	const paymentState = ref<PaymentState>('idle')
	const orderId = ref<string | null>(null)
	const expiresAt = ref<string | null>(null)
	const orderStatus = ref<OrderStatus | null>(null)
	const failureReason = ref<FailureReason | null>(null)
	/** Код, который бэк прислал, но мы про него не знаем: для лога, не для UI. */
	const unknownFailureCode = ref<string | null>(null)
	/**
	 * Была ли по этому заказу попытка списания. Единственный источник — платёжный виджет:
	 * из статуса заказа это больше не следует (см. OrderStatus). Переживает перезагрузку
	 * вместе с заказом, но не переезд в другой браузер — там ограничения не будет.
	 */
	const chargeAttempted = ref(false)
	/**
	 * Видимость шторки отдельно от состояния: человек вправе закрыть «Подтверждаем оплату»
	 * и продолжить пользоваться приложением, а поллинг при этом обязан идти дальше.
	 */
	const dialogVisible = ref(false)

	// Признак живого поллера. Отдельно от paymentState: шторку закрыли — поллинг остался.
	const polling = ref(false)

	/**
	 * Номер наблюдения. Каждый checkout создаёт новый заказ, а предыдущий протухает сам,
	 * поэтому поллер прошлого заказа обязан замолчать — и, главное, не имеет права
	 * завершить заказ, который уже сменился. Иначе исход брошенного заказа прилетит
	 * поверх нового: погасит его шторку, вычистит из хранилища или объявит ошибку.
	 *
	 * Не ref: на это число ничто не подписывается, оно только сверяется.
	 */
	let watchToken = 0

	function deadline(): number {
		const parsed = expiresAt.value ? Date.parse(expiresAt.value) : NaN
		return Number.isFinite(parsed) ? parsed : Date.now() + FALLBACK_TTL_MS
	}

	function isExpired(): boolean {
		return Date.now() >= deadline()
	}

	const isAwaitingPayment = computed(() => orderStatus.value === 'pending' && chargeAttempted.value && !isExpired())

	function persistOrder(): void {
		if (!orderId.value) return
		const stored: StoredOrder = {
			orderId: orderId.value,
			expiresAt: expiresAt.value,
			attempted: chargeAttempted.value
		}
		localStorage.setItem(STORAGE_KEYS.BILLING_PENDING_ORDER, JSON.stringify(stored))
	}

	function forgetOrder(): void {
		localStorage.removeItem(STORAGE_KEYS.BILLING_PENDING_ORDER)
	}

	function readStoredOrder(): StoredOrder | null {
		try {
			const raw = localStorage.getItem(STORAGE_KEYS.BILLING_PENDING_ORDER)
			const parsed = raw ? (JSON.parse(raw) as StoredOrder) : null
			return parsed?.orderId ? parsed : null
		} catch {
			// Мусор в хранилище не должен ронять запуск приложения.
			return null
		}
	}

	function trackOrder(id: string, expires: string | null): void {
		watchToken += 1
		polling.value = false
		orderId.value = id
		expiresAt.value = expires
		orderStatus.value = 'pending'
		failureReason.value = null
		unknownFailureCode.value = null
		// Заказ только создан: денег в игре ещё нет.
		chargeAttempted.value = false
		persistOrder()
	}

	function setState(next: PaymentState): void {
		paymentState.value = next
		// Шторка сама открывается на состояниях, которые человеку нужно увидеть.
		if (DIALOG_STATES.includes(next)) dialogVisible.value = true
		if (next === 'idle') dialogVisible.value = false
	}

	function closeDialog(): void {
		dialogVisible.value = false
	}

	/**
	 * Открыть шторку ожидания по требованию: запертая гейтом генерация или клик по кнопке
	 * оплаты, пока исход прошлого платежа неизвестен.
	 *
	 * Одного dialogVisible для этого мало: шторка рисуется только на состояниях, которые ей
	 * понятны (см. PaymentDialog), а после restore({ silent: true }) состояние остаётся idle —
	 * деньги в игре, а показать человеку нечего. Поэтому ожидание оплаты поднимает состояние.
	 */
	function showWaiting(): void {
		if (isAwaitingPayment.value && paymentState.value !== 'confirming') {
			setState('confirming')
			return
		}
		if (DIALOG_STATES.includes(paymentState.value)) dialogVisible.value = true
	}

	function toFailureReason(raw: OrderResponse['failure_reason']): FailureReason | null {
		if (!raw) return null
		const known = KNOWN_FAILURE_REASONS.find((reason) => reason === raw)
		if (known) return known
		// Список кодов открытый: неизвестный не ломает разбор, но и молчать о нём нельзя.
		unknownFailureCode.value = String(raw)
		console.warn('[billing] неизвестная причина отказа', raw)
		return null
	}

	/**
	 * Успех: сначала подтягиваем срез и профиль, только потом показываем «Готово».
	 * Обратный порядок показал бы человеку поздравление рядом со старыми нулями в лимитах.
	 */
	async function applySuccess(): Promise<void> {
		// Единая точка всех путей успеха: поллинг, restore(), cancel() → isOrderPaid().
		if (orderId.value) trackPaymentSuccess(orderId.value)
		const usage = useUsageStore()
		const profile = useProfileStore()
		await Promise.allSettled([usage.fetchUsage(), profile.fetchProfile()])
		setState('succeeded')
	}

	function applyOrder(order: OrderResponse): void {
		orderStatus.value = order.status
		if (order.expires_at) expiresAt.value = order.expires_at
		if (isAwaitingPayment.value && paymentState.value !== 'confirming') setState('confirming')
	}

	/**
	 * Поллинг статуса. Две фазы вместо одной: частый темп нужен только в начале, дальше
	 * он бессмысленно давит на рейт-лимит — при нескольких вкладках запросы утраиваются.
	 */
	async function pollOrder(id: string, token: number): Promise<OrderResponse | null> {
		const fastUntil = Date.now() + FAST_WINDOW_MS
		/** Наблюдение ещё актуально: заказ с тех пор не сменился. */
		const current = (): boolean => token === watchToken
		// Побочный эффект в предикате намеренный: это единственное место, где виден
		// каждый промежуточный ответ, а на нём открывается шторка ожидания.
		const isPending = (order: OrderResponse): boolean => {
			// Устаревшее наблюдение состояние не трогает: заказ уже другой.
			if (!current()) return false
			applyOrder(order)
			return order.status === 'pending'
		}
		const alive = (): boolean => polling.value && current() && !isExpired()

		const fast = await pollUntil(() => getOrder(id), {
			delay: FAST_INTERVAL_MS,
			shouldContinue: () => alive() && Date.now() < fastUntil,
			isPending,
			shouldRetry: isTransientPollError
		})
		if (fast) return fast
		if (!alive()) return null

		return pollUntil(() => getOrder(id), {
			delay: SLOW_INTERVAL_MS,
			shouldContinue: alive,
			isPending,
			shouldRetry: isTransientPollError
		})
	}

	/**
	 * Следим за заказом до терминального статуса. Вызывается и после оплаты, и при
	 * восстановлении незавершённого заказа после перезагрузки.
	 */
	async function watchOrder(
		id: string = orderId.value ?? '',
		expires: string | null = expiresAt.value,
		options: { silent?: boolean; attempted?: boolean } = {}
	): Promise<void> {
		if (!id) return
		// Тот же заказ уже под наблюдением — второй поллер не нужен. А вот другой заказ
		// прежнее наблюдение обязан сменить: заказы больше не переиспользуются.
		if (polling.value && orderId.value === id) return
		const token = ++watchToken
		orderId.value = id
		expiresAt.value = expires
		// attempted передаёт тот, кто знает: виджет — что деньги ушли, restore — что было
		// записано в хранилище. По статусу заказа это не восстановить.
		if (options.attempted !== undefined) chargeAttempted.value = options.attempted
		persistOrder()
		polling.value = true
		// silent — восстановление после перезагрузки: статус заказа ещё неизвестен,
		// и открывать шторку до первого ответа сервера нечестно.
		if (!options.silent) setState('confirming')

		try {
			const order = await pollOrder(id, token)
			// Пока мы ждали, заказ сменился: этот исход относится к брошенному заказу,
			// и трогать им состояние нельзя.
			if (token !== watchToken) return
			if (!order) {
				// Дедлайн вышел, а ответа нет. Деньги могли уйти, поэтому это не «ошибка».
				// TODO(billing): шторка неизвестного исхода — ждём вводных от продукта.
				finish('unknown')
				return
			}
			if (order.status === 'succeeded') {
				forgetOrder()
				polling.value = false
				orderStatus.value = 'succeeded'
				await applySuccess()
				return
			}
			if (order.status === 'failed') {
				failureReason.value = toFailureReason(order.failure_reason)
				trackPaymentFailed(id, order.failure_reason)
				// Отмену пользователем экраном ошибки не показываем: он и так знает, что закрыл окно.
				finish(failureReason.value === 'cancelled_by_user' ? 'idle' : 'failed')
				return
			}
			// Сюда попадёт только исход, которого мы не понимаем: терминальных статусов
			// на проводе два, и оба разобраны выше. Деньги могли уйти — не «ошибка».
			finish('unknown')
		} catch (error) {
			if (token !== watchToken) return
			handlePollError(error)
		}
	}

	function handlePollError(error: unknown): void {
		const api = error instanceof ApiError ? error : null
		const notFound = api?.code === BILLING_ERROR_CODES.ORDER_NOT_FOUND || api?.status === 404 || api?.status === 403
		if (notFound) {
			// Иначе фронт будет долбить в 404 до дедлайна, держа человека в ожидании.
			console.warn('[billing] заказ не найден, поллинг остановлен', api?.code)
			finish('failed')
			return
		}
		console.warn('[billing] поллинг заказа прерван', error)
		finish('unknown')
	}

	/** Завершает наблюдение: снимает ограничение генераций и чистит хранилище. */
	function finish(next: PaymentState): void {
		polling.value = false
		if (next === 'failed') orderStatus.value = 'failed'
		if (next === 'unknown') orderStatus.value = 'expired'
		forgetOrder()
		setState(next)
	}

	async function restore(): Promise<void> {
		if (polling.value) return
		const serverOrder = useUsageStore().pendingOrder
		const stored = readStoredOrder()
		// Хранилище важнее среза: в нём лежит invoiceId — тот самый id, которым заказ уже
		// опрашивался. Придёт ли в pending_order он же или собственный номер заказа бэка,
		// контрактом не закреплено, а промахнуться здесь — это 404 и шторка неудачи
		// на человеке, с которого списали деньги.
		const id = stored?.orderId ?? serverOrder?.order_id
		if (!id) return
		const attempted = stored?.orderId === id && stored?.attempted === true
		// Заказ с ушедшими деньгами запирает генерации с первой миллисекунды, а не с первого
		// ответа поллера: pending — единственное, чем бэк по такому заказу может ответить, а
		// три секунды открытого гейта человек успевает потратить на генерацию и на второй
		// заказ из профиля. Терминальный ответ это предположение перезапишет.
		if (attempted) orderStatus.value = 'pending'
		await watchOrder(id, stored?.expiresAt ?? serverOrder?.expires_at ?? null, { silent: true, attempted })
	}

	/**
	 * Успел ли заказ оплатиться до отмены. Второй предохранитель к isAttempt: «попытки
	 * не было» фронт выводит из исхода чужого виджета, а тот приносил оплату под типом,
	 * которого нет в документации, — и оплаченный заказ уходил в abort. Один лишний GET
	 * дешевле повторения этой истории.
	 *
	 * Ошибку и молчание бэка считаем за «не оплачен»: иначе человек, закрывший виджет,
	 * остался бы с заказом, который никто не погасил.
	 */
	async function isOrderPaid(): Promise<boolean> {
		const id = orderId.value
		if (!id) return false
		try {
			const order = await getOrder(id, { timeout: PAID_CHECK_TIMEOUT_MS })
			if (order.status !== 'succeeded') return false
			orderStatus.value = 'succeeded'
			forgetOrder()
			return true
		} catch (error) {
			console.warn('[billing] статус заказа перед отменой не получен', error)
			return false
		}
	}

	/**
	 * Человек закрыл платёжный виджет крестиком, не начав списание. Кроме локальной уборки
	 * гасим заказ на бэке, иначе он висит в pending до конца своего срока.
	 *
	 * Ответ ждём до сброса в idle, и это главное здесь: пока состояние widget, startCheckout
	 * не пустит второй заказ (см. RESTARTABLE_STATES). Ручка отмены тела не имеет — какой
	 * заказ гасить, решает бэк, — поэтому abort, прилетевший после нового checkout, погасил
	 * бы уже новый заказ. Таймаут запроса не даёт ожиданию стать вечным.
	 *
	 * Ошибку глушим: заказ протухнет сам по expires_at, а показывать экран ошибки тому,
	 * кто просто закрыл окно оплаты, незачем.
	 */
	async function cancel(): Promise<void> {
		if (chargeAttempted.value) return
		polling.value = false
		// Спрашиваем бэк до того, как гасить: «попытки не было» — это вывод из исхода чужого
		// виджета, и он уже однажды оказался неверным.
		if (await isOrderPaid()) {
			await applySuccess()
			return
		}
		forgetOrder()
		orderStatus.value = null
		try {
			await postAbortPayment()
		} catch (error) {
			console.warn('[billing] отмену оплаты бэк не подтвердил', error)
		}
		setState('idle')
	}

	function fail(reason: FailureReason | null = null): void {
		failureReason.value = reason
		finish('failed')
	}

	function reset(): void {
		polling.value = false
		orderId.value = null
		expiresAt.value = null
		orderStatus.value = null
		failureReason.value = null
		unknownFailureCode.value = null
		chargeAttempted.value = false
		forgetOrder()
		setState('idle')
	}

	return {
		paymentState,
		orderId,
		expiresAt,
		orderStatus,
		failureReason,
		unknownFailureCode,
		chargeAttempted,
		dialogVisible,
		polling,
		isAwaitingPayment,
		trackOrder,
		setState,
		closeDialog,
		showWaiting,
		watchOrder,
		restore,
		cancel,
		fail,
		reset
	}
})
